/* eslint-disable react/prop-types */
import { FC, useEffect, useId, useMemo, useRef, useState } from "react";
import {
	FormProvider,
	SubmitHandler,
	useForm,
	useFormState
} from "react-hook-form";
import { ArrowLeft, Upload, Bell, FileText } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "src/components/ui/button";
import {
	FormField,
	FormItem,
	FormLabel,
	FormControl
} from "src/components/ui/form";
import { Input } from "src/components/ui/input";
import { Checkbox } from "src/components/ui/checkbox";
import isEmpty from "lodash.isempty";
import {
	AnnouncementFormValues,
	BlockFileEntry
} from "../../adapters/formValuesToAnnouncement";
import { useAzureFileUpload } from "src/components/AssetUploaders/useAzureFileUpload";
import { AzureImage } from "src/components/AssetUploaders/AzureImage";
import PushPreviewPanel from "../PushPreviewPanel/PushPreviewPanel";
import AnnouncementPreview from "../AnnouncementPreview/AnnouncementPreview";
import AudiencePicker from "../AudiencePicker/AudiencePicker";
import ContentBlocksEditor, {
	ContentBlock
} from "../ContentBlocksEditor/ContentBlocksEditor";
import {
	parseBodyToBlocks,
	serialiseBlocksToBodyHtml,
	extractBlockFiles,
	serialiseBlocksForPersistence
} from "../../utils/announcementFormHelpers";
import {
	extractMentions,
	mergeMentions
} from "../RichTextEditor/extractMentions";

export type { AnnouncementFormValues };

interface Props {
	defaultValues: AnnouncementFormValues;
	onSubmit: (data: AnnouncementFormValues, imageRemoved: boolean) => void;
	onSchedule?: (data: AnnouncementFormValues, imageRemoved: boolean) => void;
	onSendNow?: (data: AnnouncementFormValues, imageRemoved: boolean) => void;
	isSubmitting?: boolean;
	isScheduling?: boolean;
	isSending?: boolean;
	isDeleting?: boolean;
	isEdit?: boolean;
	editingScheduled?: boolean;
	onDelete?: () => void;
	onRevertToDraft?: () => void;
	existingImageReference?: string | null;
}

const ALLOWED_IMAGE_TYPES = ["image/jpeg", "image/jpg", "image/png"];
const MAX_IMAGE_SIZE = 2_097_152; // 2 MB

const AnnouncementForm: FC<Props> = ({
	defaultValues,
	onSubmit,
	onSchedule,
	onSendNow,
	isSubmitting = false,
	isScheduling = false,
	isSending = false,
	isDeleting = false,
	isEdit = false,
	editingScheduled = false,
	onDelete,
	onRevertToDraft,
	existingImageReference
}) => {
	const methods = useForm<AnnouncementFormValues>({
		mode: "onChange",
		reValidateMode: "onChange",
		defaultValues
	});

	const { reset, control, handleSubmit, watch, setValue, trigger } = methods;
	const { isDirty, errors } = useFormState({ control });

	const fileInputRef = useRef<HTMLInputElement>(null);
	const [previewBlobUrl, setPreviewBlobUrl] = useState<string | null>(null);
	const [imageRemoved, setImageRemoved] = useState(false);
	const [imageError, setImageError] = useState<string | null>(null);
	const [isDragging, setIsDragging] = useState(false);
	const [contentBlocks, setContentBlocks] = useState<ContentBlock[]>(() =>
		parseBodyToBlocks(defaultValues.body, defaultValues.contentBlocks)
	);

	const notificationType = watch("notificationType");
	const isFullAnnouncement = notificationType === "announcement";

	const heroImageReference = watch("heroImage");
	const previewTitle = watch("title") ?? "";
	const pushTitle = watch("pushTitle");
	const pushBody = watch("pushBody");
	const hasSchedule = watch("hasSchedule");
	const hasExpiry = watch("hasExpiry");
	const scheduledDate = watch("scheduledDate");
	const audienceType = watch("audienceType");
	const audiencePlatform = watch("audiencePlatform");
	const audienceUserIds = watch("audienceUserIds");
	const audienceGroupIds = watch("audienceGroupIds");
	const watchedContentBlocks = watch("contentBlocks");

	// Derive mentioned user-ids from the persisted content blocks (best-effort
	// — covers only text blocks whose HTML has been serialised; images/videos
	// can't contain mentions). DOMParser runs in jsdom during tests and in the
	// browser during runtime.
	// FIX E: memoised so DOMParser only runs when the watched content changes.
	const mentionedUserIds = useMemo(
		() =>
			mergeMentions(
				(watchedContentBlocks ?? [])
					.filter((b) => b.type === "text")
					.map((b) => extractMentions(b.content ?? ""))
			).userIds,
		[watchedContentBlocks]
	);

	// FIX E: memoised; only recomputes when mentions or audience values change.
	const audienceWarning = useMemo(() => {
		if (mentionedUserIds.length === 0) return null;
		if (audienceType === "everyone") return null;
		if (audienceType === "users") {
			const outside = mentionedUserIds.filter(
				(id) => !(audienceUserIds ?? []).includes(id)
			);
			return outside.length > 0
				? "Some mentioned users are not in the selected audience; they'll still be notified."
				: null;
		}
		// groups / platform: membership not known client-side
		return "Some mentioned users may not be in the selected audience; they'll still be notified.";
	}, [mentionedUserIds, audienceType, audienceUserIds]);

	const {
		upload,
		uploading: isUploading,
		error: uploadError
	} = useAzureFileUpload((data) => {
		if (Array.isArray(data) && typeof data[0] === "string") {
			setValue("heroImage", data[0], { shouldDirty: true });
		}
	});

	useEffect(() => {
		if (uploadError) setImageError("Failed to upload image, try again.");
	}, [uploadError]);

	const hasImage =
		!imageRemoved &&
		(!!heroImageReference || !!previewBlobUrl || !!existingImageReference);

	// A content block is mid-upload when a File has been picked but no
	// persisted reference has arrived yet. Save/Send must stay disabled
	// until every block upload resolves — otherwise the announcement
	// saves with ghost blocks (empty content where the file should be).
	const hasUploadingBlock = contentBlocks.some(
		(b) => (b.type === "image" || b.type === "video") && b.file && !b.content
	);

	// A persisted video block sits in "Processing" until Bitmovin finishes
	// encoding (typically 5–10 minutes after upload). Send/Schedule must
	// stay disabled until every video reports ReadyForPlayback — sending
	// earlier means recipients receive an announcement whose videos won't
	// play. Save Draft is intentionally unaffected: an admin can save and
	// come back when processing is done. The backend enforces the same
	// gate, so this only governs UX clarity.
	const hasProcessingVideo = contentBlocks.some(
		(b) =>
			b.type === "video" &&
			!!b.content &&
			b.processingStatus !== "ReadyForPlayback"
	);

	const scheduledTime = watch("scheduledTime");
	const expiryTime = watch("expiryTime");

	// Compute scheduled instant from form fields. The native date/time
	// inputs give wall-clock strings; we treat them as the user's local
	// time (which is also London time for this admin) — same convention
	// used by the backend's ToUtcFromLondon conversion.
	const scheduledInstant = (() => {
		if (!hasSchedule || !scheduledDate) return null;
		const ms = Date.parse(`${scheduledDate}T${scheduledTime || "00:00"}:00`);
		return Number.isFinite(ms) ? ms : null;
	})();
	const hasFutureSchedule =
		scheduledInstant !== null && scheduledInstant > Date.now();

	const anyBusy =
		isSubmitting ||
		isScheduling ||
		isSending ||
		isDeleting ||
		isUploading ||
		hasUploadingBlock ||
		!!imageError;

	const isSubmitBtnDisabled =
		(!isDirty && !imageRemoved) || !isEmpty(errors) || anyBusy;

	// Schedule is only meaningful when the user has filled in a future
	// scheduled date — otherwise there's nothing to schedule. We don't
	// require isDirty because for a row already-scheduled the user may
	// just want to flip status without editing.
	const isScheduleBtnDisabled =
		!isEmpty(errors) || anyBusy || !hasFutureSchedule || hasProcessingVideo;

	// Send Now is hidden behind a future schedule so the admin doesn't
	// dispatch immediately when they meant to schedule.
	const isSendBtnDisabled =
		(!isDirty && !imageRemoved) ||
		!isEmpty(errors) ||
		anyBusy ||
		hasFutureSchedule ||
		hasProcessingVideo;

	const todayLocal = () => {
		const d = new Date();
		return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
	};

	// Thin wrapper that pushes the three derived shapes into react-
	// hook-form state. The actual serialisation is in pure helpers
	// (announcementFormHelpers.ts) so it can be unit-tested without
	// mounting the form — see `serialiseBlocksToBodyHtml`,
	// `extractBlockFiles`, `serialiseBlocksForPersistence`.
	const syncBlocksToForm = (blocks: ContentBlock[], dirty: boolean) => {
		setValue("body", serialiseBlocksToBodyHtml(blocks), { shouldDirty: dirty });
		setValue("blockFiles", extractBlockFiles(blocks), { shouldDirty: dirty });
		setValue("contentBlocks", serialiseBlocksForPersistence(blocks), {
			shouldDirty: dirty
		});
	};

	useEffect(() => {
		reset(defaultValues);
		setImageRemoved(false);
		setImageError(null);
		setPreviewBlobUrl((prev) => {
			if (prev) URL.revokeObjectURL(prev);
			return null;
		});
		const parsed = parseBodyToBlocks(
			defaultValues.body,
			defaultValues.contentBlocks
		);
		setContentBlocks(parsed);
		syncBlocksToForm(parsed, false);
	}, [defaultValues]); // eslint-disable-line

	useEffect(() => {
		trigger("scheduledDate");
		trigger("expiryDate");
	}, [
		scheduledDate,
		scheduledTime,
		expiryTime,
		hasSchedule,
		hasExpiry,
		trigger
	]);

	const handleBlocksChange = (blocks: ContentBlock[]) => {
		setContentBlocks(blocks);
		syncBlocksToForm(blocks, true);
	};

	useEffect(() => {
		if (heroImageReference && previewBlobUrl) {
			URL.revokeObjectURL(previewBlobUrl);
			setPreviewBlobUrl(null);
		}
	}, [heroImageReference, previewBlobUrl]);

	const validateAndSetFile = (file: File) => {
		if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
			setImageError("Only JPG and PNG files are allowed.");
			if (fileInputRef.current) fileInputRef.current.value = "";
			return;
		}
		if (file.size > MAX_IMAGE_SIZE) {
			setImageError("Image must be under 2 MB.");
			if (fileInputRef.current) fileInputRef.current.value = "";
			return;
		}
		setImageError(null);
		if (previewBlobUrl) URL.revokeObjectURL(previewBlobUrl);
		setPreviewBlobUrl(URL.createObjectURL(file));
		setImageRemoved(false);
		upload(file, "images", undefined, "admin-content-image");
	};

	const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const file = e.target.files?.[0];
		if (file) validateAndSetFile(file);
	};

	const handleDragOver = (e: React.DragEvent) => {
		e.preventDefault();
		setIsDragging(true);
	};

	const handleDragLeave = (e: React.DragEvent) => {
		e.preventDefault();
		setIsDragging(false);
	};

	const handleDrop = (e: React.DragEvent) => {
		e.preventDefault();
		setIsDragging(false);
		const file = e.dataTransfer.files[0];
		if (file) validateAndSetFile(file);
	};

	const handleRemoveImage = () => {
		setValue("heroImage", null, { shouldDirty: true });
		if (previewBlobUrl) URL.revokeObjectURL(previewBlobUrl);
		setPreviewBlobUrl(null);
		setImageRemoved(true);
		setImageError(null);
		if (fileInputRef.current) {
			fileInputRef.current.value = "";
		}
	};

	const renderImagePreview = () => {
		if (previewBlobUrl) {
			return (
				<img
					src={previewBlobUrl}
					alt="Hero image preview"
					className="object-contain w-full h-auto max-h-[400px]"
				/>
			);
		}
		const ref = heroImageReference ?? existingImageReference;
		if (ref) {
			return (
				<AzureImage
					uuid={ref}
					imageProfile="admin-content-image"
					alt="Hero image preview"
					className="object-contain w-full h-auto max-h-[400px]"
				/>
			);
		}
		return null;
	};

	const handleFormSubmit: SubmitHandler<AnnouncementFormValues> = (data) => {
		onSubmit(data, imageRemoved);
	};

	const handleSchedule = () => {
		handleSubmit((data) => {
			if (onSchedule) onSchedule(data, imageRemoved);
		})();
	};

	const handleSendNow = () => {
		handleSubmit((data) => {
			if (onSendNow) onSendNow(data, imageRemoved);
		})();
	};

	const detailsHasErrors = !!(errors.title || errors.body);
	const pushHasErrors = !!(errors.pushTitle || errors.pushBody);
	const audienceHasErrors = !!(
		errors.audienceType ||
		errors.audiencePlatform ||
		errors.audienceUserIds
	);
	const schedulingHasErrors = !!(errors.scheduledDate || errors.scheduledTime);
	const expiryHasErrors = !!(errors.expiryDate || errors.expiryTime);
	const scheduleHintId = useId();
	const sendNowHintId = useId();

	return (
		<form
			onSubmit={handleSubmit(handleFormSubmit)}
			onKeyDown={(e) => {
				if (e.key === "Enter" && e.target instanceof HTMLInputElement) {
					e.preventDefault();
				}
			}}
		>
			<FormProvider {...methods}>
				<div className="flex flex-col gap-4">
					{/* Type toggle */}
					<fieldset>
						<legend className="sr-only">Notification type</legend>
						<div className="inline-flex w-full gap-1 p-1.5 bg-white border border-border rounded-xl shadow-xs">
							{(
								[
									{
										value: "push",
										icon: <Bell className="h-4 w-4" />,
										label: "Push Only"
									},
									{
										value: "announcement",
										icon: <FileText className="h-4 w-4" />,
										label: "Announcement"
									}
								] as const
							).map((option) => {
								const isChecked = notificationType === option.value;
								return (
									<label key={option.value} className="flex-1 cursor-pointer">
										<input
											type="radio"
											className="sr-only"
											value={option.value}
											checked={isChecked}
											onChange={() =>
												setValue("notificationType", option.value, {
													shouldDirty: true
												})
											}
										/>
										<div
											className={[
												"flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all duration-200",
												isChecked
													? "bg-primary text-white shadow-md shadow-primary/50"
													: "text-muted-foreground hover:text-foreground hover:bg-white/80"
											].join(" ")}
										>
											{option.icon}
											<span>{option.label}</span>
										</div>
									</label>
								);
							})}
						</div>
					</fieldset>

					{/* Card 1: Details (announcement only) */}
					{isFullAnnouncement && (
						<div
							className={[
								"rounded-xl border bg-subtle shadow-sm transition-colors",
								detailsHasErrors ? "border-red-400/60" : "border-border"
							].join(" ")}
						>
							{detailsHasErrors && (
								<div className="relative">
									<span
										role="alert"
										className="absolute -top-2.5 right-3 bg-red-500 text-white text-xs px-2 py-0.5 rounded-full font-medium"
									>
										Please fill out the required fields
									</span>
								</div>
							)}
							<div className="p-6">
								<h3 className="font-heading text-lg font-medium text-foreground mb-4">
									Details
								</h3>

								<div className="flex flex-col gap-5">
									<FormField
										control={control}
										name="title"
										rules={{
											required: "Title is required",
											maxLength: {
												value: 100,
												message: "Maximum 100 characters"
											}
										}}
										render={({ field }) => (
											<FormItem>
												<FormLabel className="text-sm font-medium text-zinc-600">
													Title
												</FormLabel>
												<FormControl>
													<Input
														placeholder="Announcement title"
														maxLength={100}
														className="w-full bg-white"
														{...field}
													/>
												</FormControl>
												<p className="text-[13px] text-zinc-600 text-right mt-1">
													{field.value?.length || 0} / 100
												</p>
											</FormItem>
										)}
									/>

									{/* Hero image */}
									<div>
										<p className="text-sm font-medium text-zinc-600 mb-2">
											Hero Image (optional)
										</p>
										<p className="text-[13px] text-muted-foreground mb-3">
											JPG or PNG, 1000 x 750px recommended, under 2 MB
										</p>
										<input
											ref={fileInputRef}
											type="file"
											accept="image/jpeg,image/jpg,image/png"
											className="hidden"
											tabIndex={-1}
											aria-hidden="true"
											onChange={handleFileChange}
										/>
										<div className="max-w-[480px]">
											{hasImage ? (
												<>
													<div className="mb-3 rounded-lg overflow-hidden border border-border bg-subtle max-h-[400px]">
														{renderImagePreview()}
													</div>
													{isUploading && (
														<p className="text-xs text-muted-foreground mb-2">
															Uploading…
														</p>
													)}
													<div className="flex items-center gap-2">
														<button
															type="button"
															className="inline-flex items-center justify-center px-5 h-9 rounded-lg border border-foreground bg-white text-foreground text-sm font-medium cursor-pointer hover:bg-foreground hover:text-white transition-colors"
															onClick={() => fileInputRef.current?.click()}
														>
															Change image
														</button>
														<button
															type="button"
															className="inline-flex items-center justify-center px-5 h-9 rounded-lg border border-red-300 bg-white text-red-600 text-sm font-medium cursor-pointer hover:bg-red-600 hover:text-white hover:border-red-600 transition-colors"
															onClick={handleRemoveImage}
														>
															Remove
														</button>
													</div>
												</>
											) : (
												<div
													role="button"
													tabIndex={0}
													onClick={() => fileInputRef.current?.click()}
													onKeyDown={(e) => {
														if (e.key === "Enter" || e.key === " ") {
															e.preventDefault();
															fileInputRef.current?.click();
														}
													}}
													onDragOver={handleDragOver}
													onDragLeave={handleDragLeave}
													onDrop={handleDrop}
													className={[
														"flex flex-col items-center justify-center gap-3 rounded-lg border-2 border-dashed px-6 py-10 text-center transition-colors cursor-pointer",
														isDragging
															? "border-primary bg-primary/10"
															: "border-border bg-white hover:border-primary hover:bg-primary/5"
													].join(" ")}
												>
													<Upload className="h-6 w-6 text-muted-foreground" />
													<div>
														<p className="text-sm font-medium text-foreground">
															Drag image here or click to browse
														</p>
														<p className="text-xs text-muted-foreground mt-1">
															JPG or PNG, under 2 MB
														</p>
													</div>
												</div>
											)}
										</div>
										{imageError && (
											<p className="text-sm text-red-500 mt-2" role="alert">
												{imageError}
											</p>
										)}
									</div>

									{/* Content blocks */}
									<div>
										<p className="text-sm font-medium text-zinc-600 mb-2">
											Content
										</p>
										<p className="text-[13px] text-muted-foreground mb-3">
											Add and reorder text, images, and videos
										</p>
										<ContentBlocksEditor
											blocks={contentBlocks}
											onChange={handleBlocksChange}
										/>
									</div>
								</div>
							</div>
						</div>
					)}

					{/* Live preview — visible only for full Announcements (push-only
					    notifications have no in-app preview to show). */}
					{isFullAnnouncement && (
						<div className="rounded-xl border border-border bg-subtle shadow-sm">
							<div className="p-6">
								{/* `<label>` is for form controls; the Preview card is a
								    section with no associated input, so a heading is the
								    correct semantic container and keeps the card in the
								    page's heading outline for screen-reader users. */}
								<h3 className="font-heading text-lg font-medium text-foreground mb-1">
									Preview
								</h3>
								<p className="text-[13px] text-muted-foreground mb-4">
									How the announcement will appear to users in the app.
								</p>
								<AnnouncementPreview
									title={previewTitle}
									heroImageRef={
										heroImageReference ?? existingImageReference ?? null
									}
									heroImagePreviewUrl={previewBlobUrl}
									blocks={contentBlocks}
								/>
							</div>
						</div>
					)}

					{/* Card 2: Push Notification */}
					<div
						className={[
							"rounded-xl border bg-subtle shadow-sm transition-colors",
							pushHasErrors ? "border-red-400/60" : "border-border"
						].join(" ")}
					>
						{pushHasErrors && (
							<div className="relative">
								<span
									role="alert"
									className="absolute -top-2.5 right-3 bg-red-500 text-white text-xs px-2 py-0.5 rounded-full font-medium"
								>
									Please fill out the required fields
								</span>
							</div>
						)}
						<div className="p-6">
							<h3 className="font-heading text-lg font-medium text-foreground mb-1">
								Push Notification
							</h3>
							<p className="text-[13px] text-muted-foreground mb-4">
								These fields populate the push notification sent to devices
							</p>

							<div className="flex flex-col gap-5">
								<FormField
									control={control}
									name="pushTitle"
									rules={{
										required: "Push title is required",
										maxLength: {
											value: 50,
											message: "Maximum 50 characters"
										}
									}}
									render={({ field }) => (
										<FormItem>
											<FormLabel className="text-sm font-medium text-zinc-600">
												Push Title
											</FormLabel>
											<FormControl>
												<Input
													placeholder="Notification title"
													maxLength={50}
													className="w-full bg-white"
													{...field}
												/>
											</FormControl>
											<p className="text-[13px] text-zinc-600 text-right mt-1">
												{field.value?.length || 0} / 50
											</p>
										</FormItem>
									)}
								/>

								<FormField
									control={control}
									name="pushBody"
									rules={{
										maxLength: {
											value: 150,
											message: "Maximum 150 characters"
										}
									}}
									render={({ field }) => (
										<FormItem>
											<FormLabel className="text-sm font-medium text-zinc-600">
												Push Body
											</FormLabel>
											<FormControl>
												<textarea
													placeholder="Short notification summary"
													maxLength={150}
													rows={3}
													className="flex w-full rounded-md border border-input bg-white px-3 py-2 text-base ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 overflow-hidden resize-none"
													onInput={(e) => {
														const target = e.target as HTMLTextAreaElement;
														target.style.height = "auto";
														target.style.height = `${target.scrollHeight}px`;
													}}
													{...field}
												/>
											</FormControl>
											<p className="text-[13px] text-zinc-600 text-right mt-1">
												{field.value?.length || 0} / 150
											</p>
										</FormItem>
									)}
								/>
								{/* Inline push preview */}
								<div className="mt-2">
									<PushPreviewPanel
										pushTitle={pushTitle || ""}
										pushBody={pushBody || ""}
									/>
								</div>
							</div>
						</div>
					</div>

					{/* Card 3: Audience */}
					<div
						className={[
							"rounded-xl border bg-subtle shadow-sm transition-colors",
							audienceHasErrors ? "border-red-400/60" : "border-border"
						].join(" ")}
					>
						{audienceHasErrors && (
							<div className="relative">
								<span
									role="alert"
									className="absolute -top-2.5 right-3 bg-red-500 text-white text-xs px-2 py-0.5 rounded-full font-medium"
								>
									Please select an audience
								</span>
							</div>
						)}
						<div className="p-6">
							<h3 className="font-heading text-lg font-medium text-foreground mb-4">
								Audience
							</h3>

							<AudiencePicker
								audienceType={audienceType}
								audiencePlatform={audiencePlatform}
								audienceUserIds={audienceUserIds}
								audienceGroupIds={audienceGroupIds ?? []}
								onAudienceTypeChange={(type) =>
									setValue("audienceType", type, { shouldDirty: true })
								}
								onPlatformChange={(platform) =>
									setValue("audiencePlatform", platform, {
										shouldDirty: true
									})
								}
								onUserIdsChange={(ids) =>
									setValue("audienceUserIds", ids, { shouldDirty: true })
								}
								onGroupIdsChange={(ids) =>
									setValue("audienceGroupIds", ids, { shouldDirty: true })
								}
							/>
							{audienceWarning && (
								<p
									role="status"
									aria-live="polite"
									className="mt-3 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2"
									data-testid="audience-coverage-warning"
								>
									{audienceWarning}
								</p>
							)}
						</div>
					</div>

					{/* Card 4: Scheduling */}
					<div
						className={[
							"rounded-xl border bg-subtle shadow-sm transition-colors",
							schedulingHasErrors ? "border-red-400/60" : "border-border"
						].join(" ")}
					>
						{schedulingHasErrors && (
							<div className="relative">
								<span
									role="alert"
									className="absolute -top-2.5 right-3 bg-red-500 text-white text-xs px-2 py-0.5 rounded-full font-medium"
								>
									{errors.scheduledDate?.message ||
										"Please fill out the required fields"}
								</span>
							</div>
						)}
						<div className="p-6">
							<h3 className="font-heading text-lg font-medium text-foreground mb-4">
								Scheduling
							</h3>

							<div className="flex flex-col gap-4">
								<FormField
									control={control}
									name="hasSchedule"
									render={({ field }) => (
										<FormItem className="flex items-center gap-2 space-y-0">
											<FormControl>
												<Checkbox
													id="hasSchedule"
													checked={field.value}
													onCheckedChange={(checked) => {
														field.onChange(checked);
														// Clear the inputs on uncheck so the
														// payload (and the future-date guard)
														// reflects what the UI is showing.
														if (!checked) {
															setValue("scheduledDate", "", {
																shouldDirty: true
															});
															setValue("scheduledTime", "", {
																shouldDirty: true
															});
														}
													}}
												/>
											</FormControl>
											<FormLabel
												htmlFor="hasSchedule"
												className="!mt-0 text-sm font-normal leading-4 text-foreground cursor-pointer"
											>
												Schedule for later
											</FormLabel>
										</FormItem>
									)}
								/>

								{hasSchedule && (
									<div className="grid grid-cols-1 md:grid-cols-2 gap-4 ml-6">
										<FormField
											control={control}
											name="scheduledDate"
											rules={{
												required: hasSchedule
													? "Scheduled date is required"
													: false,
												validate: (value: string) => {
													if (!hasSchedule || !value) return true;
													const time = watch("scheduledTime") || "00:00";
													const scheduled = new Date(`${value}T${time}`);
													if (scheduled <= new Date()) {
														return "Scheduled date must be in the future";
													}
													return true;
												}
											}}
											render={({ field }) => (
												<FormItem>
													<FormLabel className="text-sm font-medium text-zinc-600">
														Date
													</FormLabel>
													<FormControl>
														<Input
															type="date"
															min={todayLocal()}
															className="w-full bg-white"
															{...field}
														/>
													</FormControl>
												</FormItem>
											)}
										/>

										<FormField
											control={control}
											name="scheduledTime"
											render={({ field }) => (
												<FormItem>
													<FormLabel className="text-sm font-medium text-zinc-600">
														Time
													</FormLabel>
													<FormControl>
														<Input
															type="time"
															className="w-full bg-white"
															{...field}
														/>
													</FormControl>
												</FormItem>
											)}
										/>
									</div>
								)}
							</div>
						</div>
					</div>

					{/* Card 5: Expiry */}
					<div
						className={[
							"rounded-xl border bg-subtle shadow-sm transition-colors",
							expiryHasErrors ? "border-red-400/60" : "border-border"
						].join(" ")}
					>
						{expiryHasErrors && (
							<div className="relative">
								<span
									role="alert"
									className="absolute -top-2.5 right-3 bg-red-500 text-white text-xs px-2 py-0.5 rounded-full font-medium"
								>
									{errors.expiryDate?.message ||
										"Please fill out the required fields"}
								</span>
							</div>
						)}
						<div className="p-6">
							<h3 className="font-heading text-lg font-medium text-foreground mb-4">
								Expiry
							</h3>

							<div className="flex flex-col gap-4">
								<FormField
									control={control}
									name="hasExpiry"
									render={({ field }) => (
										<FormItem className="flex items-center gap-2 space-y-0">
											<FormControl>
												<Checkbox
													id="hasExpiry"
													checked={field.value}
													onCheckedChange={field.onChange}
												/>
											</FormControl>
											{/* leading-4 makes the label's line-box match the
											    16px checkbox height so the two are the same
											    vertical size — items-center then lands both on
											    the same cross-axis centre cleanly.
											    font-normal overrides FormLabel's default
											    font-medium so the weight matches the radio
											    labels in the Scheduling card above. */}
											<FormLabel
												htmlFor="hasExpiry"
												className="!mt-0 text-sm font-normal leading-4 text-foreground cursor-pointer"
											>
												Set an expiry date
											</FormLabel>
										</FormItem>
									)}
								/>

								{hasExpiry && (
									<div className="grid grid-cols-1 md:grid-cols-2 gap-4 ml-6">
										<FormField
											control={control}
											name="expiryDate"
											rules={{
												required: hasExpiry ? "Expiry date is required" : false,
												validate: (value: string) => {
													if (!hasExpiry) return true;
													const scheduledDateValue = watch("scheduledDate");
													const scheduledTimeValue = watch("scheduledTime");
													const expiryTime = watch("expiryTime");
													if (value) {
														const expiry = new Date(
															`${value}T${expiryTime || "00:00"}`
														);
														const compareToSchedule =
															hasSchedule && scheduledDateValue;
														const baseline = compareToSchedule
															? new Date(
																	`${scheduledDateValue}T${scheduledTimeValue || "00:00"}`
																)
															: new Date();
														if (expiry <= baseline) {
															return compareToSchedule
																? "Expiry must be after scheduled date"
																: "Expiry must be in the future";
														}
													}
													return true;
												}
											}}
											render={({ field }) => (
												<FormItem>
													<FormLabel className="text-sm font-medium text-zinc-600">
														Expiry date
													</FormLabel>
													<FormControl>
														<Input
															type="date"
															min={todayLocal()}
															className="w-full bg-white"
															{...field}
														/>
													</FormControl>
												</FormItem>
											)}
										/>

										<FormField
											control={control}
											name="expiryTime"
											render={({ field }) => (
												<FormItem>
													<FormLabel className="text-sm font-medium text-zinc-600">
														Expiry time
													</FormLabel>
													<FormControl>
														<Input
															type="time"
															className="w-full bg-white"
															{...field}
														/>
													</FormControl>
												</FormItem>
											)}
										/>
									</div>
								)}
							</div>
						</div>
					</div>
				</div>

				{/* Footer */}
				<div className="flex items-center justify-between pt-8 mt-8 border-t border-border">
					<div className="flex items-center gap-4">
						<Link
							to="/announcements"
							className="inline-flex items-center gap-1.5 text-sm text-zinc-600 hover:text-foreground transition-colors"
						>
							<ArrowLeft className="h-4 w-4" />
							Back to announcements
						</Link>

						{isEdit && onDelete && (
							<Button type="button" variant="destructive" onClick={onDelete}>
								Delete
							</Button>
						)}

						{editingScheduled && onRevertToDraft && (
							<Button
								type="button"
								variant="outline"
								onClick={onRevertToDraft}
								disabled={anyBusy}
							>
								Revert to draft
							</Button>
						)}
					</div>

					<div className="flex items-center gap-3">
						{hasProcessingVideo && (
							<p
								role="status"
								aria-live="polite"
								className="text-sm text-muted-foreground mr-2"
								data-testid="video-processing-notice"
							>
								Video still processing — Send and Schedule are disabled until
								processing completes.
							</p>
						)}
						<Button
							type="submit"
							variant="outline"
							disabled={isSubmitBtnDisabled}
						>
							{isSubmitting ? (
								<>
									<div
										className="w-4 h-4 border-2 border-foreground border-t-transparent rounded-full animate-spin"
										aria-hidden="true"
									/>
									<span className="sr-only">Saving...</span>
								</>
							) : editingScheduled ? (
								"Save Changes"
							) : (
								"Save Draft"
							)}
						</Button>

						{onSchedule && (
							<>
								<span id={scheduleHintId} className="sr-only">
									Set a future date and time to schedule
								</span>
								<Button
									type="button"
									onClick={handleSchedule}
									disabled={isScheduleBtnDisabled}
									aria-describedby={
										!hasFutureSchedule ? scheduleHintId : undefined
									}
								>
									{isScheduling ? (
										<>
											<div
												className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"
												aria-hidden="true"
											/>
											<span className="sr-only">Scheduling...</span>
										</>
									) : (
										"Schedule"
									)}
								</Button>
							</>
						)}

						{onSendNow && (
							<>
								<span id={sendNowHintId} className="sr-only">
									Use Schedule when a future date is set
								</span>
								<Button
									type="button"
									onClick={handleSendNow}
									disabled={isSendBtnDisabled}
									aria-describedby={
										hasFutureSchedule ? sendNowHintId : undefined
									}
								>
									{isSending ? (
										<>
											<div
												className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"
												aria-hidden="true"
											/>
											<span className="sr-only">Sending...</span>
										</>
									) : (
										"Send Now"
									)}
								</Button>
							</>
						)}
					</div>
				</div>
			</FormProvider>
		</form>
	);
};

export default AnnouncementForm;
